import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import Dialog from '../../components/Dialog';
import { getAdConfig, recordAdStart, reportAdEnd, claimAdReward } from '../../api/ads';
import Loading from './Loading';
import './AdModal.css';

interface AdModalProps {
  visible: boolean;
  placementId?: string; // Optional: if provided by task
  onClose: () => void;
  onComplete: () => void;
}

const AdModal: React.FC<AdModalProps> = ({ visible, placementId, onClose, onComplete }) => {
  const { t } = useTranslation(['game']);
  const [timeLeft, setTimeLeft] = useState(5); // Default 5s, will update from config
  const [isFinished, setIsFinished] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [adRecordId, setAdRecordId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRewardLimitReached, setIsRewardLimitReached] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  const [adConfig, setAdConfig] = useState<any>(null); // Store fetched config
  
  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

    const getImgSrc = (name: string) => {
      if (!name) return '';
      if (name.startsWith('http') || name.startsWith('//')) {
        return name;
      }
      return getImageUrl('ad/' + name); // Return direct URL for img src, not css url()
    };
  useEffect(() => {
    if (visible) {
      // Reset state on open
      setTimeLeft(5);
      setIsFinished(false);
      setShowExitDialog(false);
      setAdRecordId(null);
      setIsRewardLimitReached(false);
      setAdConfig(null);
      
      initAd();
    } else {
      stopTimer();
    }
    return () => stopTimer();
  }, [visible]);

  const initAd = async () => {
    setLoading(true);
    try {
      let currentPlacementId = placementId;
      let duration = 5;

      // If no placementId provided, fetch config
      let config = null;
      // Always fetch config to get media details
      config = await getAdConfig();
      setAdConfig(config);
      
      if (!currentPlacementId) {
        currentPlacementId = config.adId?.toString();
      }
      duration = config.minDuration || 5;

      // Check daily limit
      let limitReached = false;
      if (config && config.dailyLimit !== undefined && config.todayCount !== undefined) {
        if (config.todayCount >= config.dailyLimit) {
          limitReached = true;
          setIsRewardLimitReached(true);
        }
      }

      // Record start only if limit not reached
      if (currentPlacementId) {
        if (!limitReached) {
          // Pass string ID if backend expects string, but adId is number in new interface.
          // recordAdStart expects string placementId.
          const id = await recordAdStart(currentPlacementId.toString());
          setAdRecordId(id);
        }
        
        // Update timer duration
        setTimeLeft(duration);
        startTimeRef.current = Date.now();
        startTimer();
      } else {
        throw new Error('No ad configuration found');
      }
    } catch (error) {
      console.error('Failed to init ad', error);
      toast.error(t('ad.loadFailed', 'Failed to load ad'));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          stopTimer();
          setIsFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleCloseAttempt = () => {
    if (!isFinished) {
      // Pause timer? Or let it run? Usually ads pause.
      stopTimer();
      setShowExitDialog(true);
    } else {
      onClose();
    }
  };

  const handleExitConfirm = () => {
    setShowExitDialog(false);
    onClose();
  };

  const handleExitCancel = () => {
    setShowExitDialog(false);
    // Resume timer if not finished
    if (!isFinished && timeLeft > 0) {
      startTimer();
    }
  };

  const handleConfirmClick = async () => {
    if (loading) return; // Prevent double click

    if (isFinished) {
      // If reward limit reached, just close
      if (isRewardLimitReached || !adRecordId) {
        onComplete();
        onClose();
        return;
      }

      if (adRecordId) {
        setLoading(true);
        try {
          // Calculate actual duration
          const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
          
          // Report end
          const claimToken = await reportAdEnd(adRecordId, duration);
          
          // If claimToken is returned, claim the ad reward
          if (claimToken) {
              const reward = await claimAdReward(adRecordId, claimToken);
              toast.success(t('ad.rewardClaimed', { defaultValue: 'Ad reward: +{{reward}}', reward }));
          }
          
          onComplete();
          onClose();
        } catch (error) {
          console.error('Failed to report ad end', error);
          toast.error(t('ad.reportFailed', 'Failed to report ad completion'));
        } finally {
          setLoading(false);
        }
      }
    }
  };

  if (!visible && !showExitDialog) return null;

  return (
    <>
      <div className={`ad-modal-container ${visible ? 'visible' : ''}`}>
        <div className="ad-scroll-container">
          <div className="ad-main-frame">
            {/* Background */}
            <div className="ad-background" style={{ backgroundImage: getImg('ad_background.png') }} />

            {/* Panel */}
            <div className="ad-panel" style={{ backgroundImage: getImg('ad_panel.png') }}>
              
              {loading && <Loading />}

              {/* Close Button */}
              <div 
                className="ad-close-button" 
                style={{ backgroundImage: getImg('ad_close_button.png') }} 
                onClick={handleCloseAttempt}
              />

              {/* Title Bar */}
              <div className="ad-title-bar" style={{ backgroundImage: getImg('ad_title_bar.png') }} />
              <div className="ad-title-text">{t('ad.title')}</div>

              {/* Content Area */}
              <div className="ad-content-area">
                 {/* Render media based on type */}
                 {adConfig?.mediaUrl ? (
                   adConfig.mediaType === 'video' ? (
                     <video 
                       src={getImgSrc(adConfig.mediaUrl)} 
                       className="ad-media-content"
                       autoPlay 
                       muted // Usually required for autoplay
                       loop 
                       playsInline
                       style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                     />
                   ) : (
                     <img 
                       src={getImgSrc(adConfig.mediaUrl)} 
                       alt={adConfig.title || 'Ad'} 
                       className="ad-media-content"
                       style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                     />
                   )
                 ) : (
                   <div className="ad-content-placeholder" style={{ backgroundImage: getImgSrc('ad_content_area.png') }}>
                      {isFinished ? t('ad.finished') : t('ad.playing')}
                   </div>
                 )}
                 
                 
              </div>

              {/* Timer */}
              {!isFinished && (
                <div className="ad-timer-container">
                  <div className="ad-timer-icon" style={{ backgroundImage: getImg('ad_timer_icon.png') }} />
                  <span className="ad-timer-text">{timeLeft}s</span>
                </div>
              )}

              {/* Confirm Button */}
              <div 
                className="ad-confirm-button" 
                style={{ 
                  backgroundImage: getImg('ad_confirm_button.png'),
                  opacity: isFinished ? 1 : 0.6,
                  filter: isFinished ? 'none' : 'grayscale(100%)'
                }}
                onClick={handleConfirmClick}
              >
                 <span className="ad-confirm-text">{isFinished ? t('ad.claim') : t('ad.watching')}</span>
              </div>
              
              {isRewardLimitReached && (
                 <div className="ad-limit-warning" style={{ 
                    position: 'absolute', 
                    bottom: '0.45rem', 
                    width: '100%', 
                    textAlign: 'center',
                    color: '#ff6b6b',
                    fontSize: '0.24rem',
                    fontWeight: 'bold',
                    textShadow: '1px 1px 2px black'
                 }}>
                   {t('ad.limitReached', 'Daily reward limit reached')}
                 </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <Dialog
        isOpen={showExitDialog}
        title={t('ad.exitTitle')}
        message={t('ad.exitMessage')}
        confirmText={t('ad.continue')}
        cancelText={t('ad.giveUp')}
        onConfirm={handleExitCancel} // "Confirm" here means "Stay" (Positive action)
        onCancel={handleExitConfirm} // "Cancel" here means "Exit" (Negative action)
        showCancel={true}
      />
    </>
  );
};

export default AdModal;
